import { FC } from 'react'
import Image from 'next/image'
import styled, { useTheme } from 'styled-components'
import AnimatedExpandIcon from '@/components/atoms/animatedExpandIcon'
import { Tiny2 } from '@/components/atoms/typography'
import { Box, Flex } from '@/components/atoms/Grid'
import { NextArrowIconDefault } from '../atoms/icons/NextArrowIconDefault'
import { SlugPathItem } from '@/types/slugPath'
import { IconButtonReverse } from './iconButtons/iconButton'
import { DiscountCouponIconDefault } from '../atoms/icons/DiscountCouponIconDefault'

export enum ArrowState {
  opened,
  closed,
  next,
  none
}

type MobileMenuItemRedesignProps = {
  title: string
  subtitle?: string
  imageUrl?: string
  arrowState?: ArrowState
  onClick: () => void
  isMain?: boolean
  highlight?: boolean
  badge?: string
  isActive?: SlugPathItem
  isBigButton?: boolean
} & Rest

const MobileMenuBoxRedesign: FC<MobileMenuItemRedesignProps> = ({
  title,
  subtitle,
  imageUrl,
  onClick,
  arrowState = ArrowState.none,
  isMain = true,
  highlight = false,
  badge,
  isActive,
  isBigButton,
  ...rest
}) => {
  const theme = useTheme()
  return (
    <MobileMenuWrapper
      isMain={isMain}
      onClick={onClick}
      justifyContent="space-between"
      {...rest}
    >
      {!isBigButton ? (
        <Flex justifyContent="center" alignItems="start" flexDirection="column">
          <Flex alignItems="center">
            <StyledBox withBadge={badge} isActive={isActive}>
              {isMain ? (
                <StyledCapitalized
                  highlight={highlight}
                  m={0}
                  isActive={isActive}
                  subtitle={subtitle}
                >
                  {title}
                </StyledCapitalized>
              ) : (
                <StyledTiny2 highlight={highlight} m={0}>
                  {title.charAt(0).toUpperCase() + title.slice(1).toLowerCase()}
                </StyledTiny2>
              )}
              {subtitle && (
                <StyledSubtitle mt="4px" mb={0}>
                  {subtitle}
                </StyledSubtitle>
              )}
            </StyledBox>
          </Flex>
        </Flex>
      ) : (
        <StyledIconButtonReverse
          title={title.toUpperCase()}
          IconElement={
            <DiscountCouponIconDefault
              color={theme.colors.accent}
              secondarycolor={theme.colors.menuBackground}
            />
          }
        />
      )}

      <Flex flexDirection="row" mr={arrowState === ArrowState.none ? 0 : 3}>
        {imageUrl?.length > 0 && !isBigButton && (
          <Image src={imageUrl} width={86} height={50} unoptimized />
        )}
        {arrowState !== ArrowState.none && makeArrowIcon(arrowState)}
      </Flex>
    </MobileMenuWrapper>
  )
}

export default MobileMenuBoxRedesign

const makeArrowIcon = (expanderState: ArrowState): JSX.Element => {
  switch (expanderState) {
    case ArrowState.next:
      return <NextArrowIconDefault width={6} height={12} opacity={0.54} />
    case ArrowState.opened:
    case ArrowState.closed:
      return (
        <AnimatedExpandIcon
          isOpened={expanderState === ArrowState.opened}
          data-testid="mobileMenuArrowDownUpIcon"
        />
      )
  }
}

const MobileMenuWrapper = styled(Flex)`
  position: relative;
  height: 55px;
  width: 100%;
  background-color: ${(props) => props.theme.colors.background};
  &:hover {
    cursor: pointer;
  }
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 100vw;
    border-bottom: 0.5px solid ${(props) => props.theme.colors.navMenuGrey};
    pointer-events: none;
  }
`

const StyledTiny2 = styled(Tiny2)`
  font-weight: 500;
  font-size: 14px;
  color: ${(props) =>
    props.highlight
      ? props.theme.colors.accent
      : props.theme.colors.onBackground};
`

const StyledSubtitle = styled(Tiny2)`
  font-size: 10px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.buttonColorNew};
`

const StyledCapitalized = styled(Tiny2)`
  color: ${(props) =>
    props.highlight
      ? props.theme.colors.accent
      : props.theme.colors.onBackground};
  padding-bottom: ${(props) => (!props.subtitle ? '10px' : '0')};
  padding-top: ${(props) => (!props.subtitle ? '9px' : '0')};
  font-size: 14px;
  &::after {
    content: '';
    display: ${(props) => (props.isActive ? 'block' : 'none')};
    width: 16px;
    height: 2px;
    background-color: ${(props) =>
      props.highlight
        ? props.theme.colors.accent
        : props.theme.colors.onBackground};
  }
`

const StyledBox = styled(Box)`
  padding: 10px 12px;
  ${(props) =>
    props.withBadge
      ? `        
        padding-left: 0px;        
      `
      : ''};
`

const StyledIconButtonReverse = styled(IconButtonReverse)`
  background-color: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.menuBackground};
  width: 100%;
  min-height: 50px;
  &:hover {
    background-color: ${(props) => props.theme.colors.accent};
    color: ${(props) => props.theme.colors.menuBackground};
  }
`
